import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ScheduledReportTypes } from '../types/scheduledReport.types';

dayjs.extend(utc);
dayjs.extend(timezone);

export const SOURCE_OPTIONS: { label: string; value: ScheduledReportTypes.Source }[] = [
    { label: 'POM Users', value: 'pomUsers' },
    { label: 'Assigned Devices', value: 'assignedDevices' },
    { label: 'Unassigned Devices', value: 'unassignedDevices' },
    { label: 'Alerts', value: 'alerts' },
    { label: 'Groups Activity', value: 'groupsActivity' },
    { label: 'Teams Activity', value: 'teamsActivity' },
    { label: 'User Activity', value: 'userActivity' },
    { label: 'POM Program Report (Excel)', value: 'programReport' },
];
// Sources whose output has fixed windows (the Date Window field does not apply).
export const WINDOWLESS_SOURCES: ReadonlySet<ScheduledReportTypes.Source> = new Set<ScheduledReportTypes.Source>(['pomUsers', 'programReport']);
export const WINDOWLESS_HELP: Partial<Record<ScheduledReportTypes.Source, string>> = {
    pomUsers: 'Not applicable — POM Users always reports the current roster.',
    programReport: 'Not applicable — the Program Report always covers the last 7 and 30 days.',
};
// Sources that honor the per-schedule group / team / tag scope (the CSV
// sources adopt it as a fast-follow).
export const FILTERABLE_SOURCES: ReadonlySet<ScheduledReportTypes.Source> = new Set<ScheduledReportTypes.Source>(['programReport']);
export const EMPTY_FILTERS: Required<ScheduledReportTypes.Filters> = { groupIds: [], teamIds: [], tagIds: [] };
// "Whole organization" or e.g. "2 groups, 1 team, 3 tags".
export const describeScope = (f?: ScheduledReportTypes.Filters | null): string => {
    const parts: string[] = [];
    const n = (list: string[] | undefined, one: string, many: string) => {
        if (list?.length) parts.push(`${list.length} ${list.length === 1 ? one : many}`);
    };
    n(f?.groupIds, 'group', 'groups');
    n(f?.teamIds, 'team', 'teams');
    n(f?.tagIds, 'tag', 'tags');
    return parts.length ? parts.join(', ') : 'Whole organization';
};
// Sources hidden unless the customer's effective setting (canonical settings-v2
// key, read via useEffectiveSettings) is true.
export const FLAGGED_SOURCES: Partial<Record<ScheduledReportTypes.Source, string>> = {
    programReport: 'safetyHub.reports.programReport.enabled',
};

export const WINDOW_OPTIONS: { label: string; value: ScheduledReportTypes.Window }[] = [
    { label: 'All (current snapshot)', value: 'all' },
    { label: 'Last 7 Days', value: '7d' },
    { label: 'Last 14 Days', value: '14d' },
    { label: 'Last 30 Days', value: '30d' },
    { label: 'Last 90 Days', value: '90d' },
];

export const CADENCE_OPTIONS: { label: string; value: ScheduledReportTypes.Cadence }[] = [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
];

export const SOURCE_LABELS: Record<ScheduledReportTypes.Source, string> = Object.fromEntries(
    SOURCE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<ScheduledReportTypes.Source, string>;

export const WINDOW_LABELS: Record<ScheduledReportTypes.Window, string> = Object.fromEntries(
    WINDOW_OPTIONS.map((o) => [o.value, o.label]),
) as Record<ScheduledReportTypes.Window, string>;

export const CADENCE_LABELS: Record<ScheduledReportTypes.Cadence, string> = Object.fromEntries(
    CADENCE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<ScheduledReportTypes.Cadence, string>;

export const DAY_OF_WEEK_OPTIONS: { label: string; value: number }[] = [
    { label: 'Sunday', value: 0 },
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 },
];

const ordinal = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
// 1–28 only, so the chosen day exists in every month (no Feb/30-day edge cases).
export const DAY_OF_MONTH_OPTIONS: { label: string; value: number }[] =
    Array.from({ length: 28 }, (_, i) => ({ label: ordinal(i + 1), value: i + 1 }));

// Hourly options, value '00:00'..'23:00', labels in 12h.
export const TIME_OPTIONS: { label: string; value: string }[] =
    Array.from({ length: 24 }, (_, h) => {
        const ampm = h < 12 ? 'AM' : 'PM';
        const h12 = h % 12 === 0 ? 12 : h % 12;
        return { label: `${h12}:00 ${ampm}`, value: `${String(h).padStart(2, '0')}:00` };
    });

// CVS is US-based; a curated list keeps the picker usable vs the full IANA db.
export const TIMEZONE_OPTIONS: { label: string; value: string }[] = [
    { label: 'Eastern (ET)', value: 'America/New_York' },
    { label: 'Central (CT)', value: 'America/Chicago' },
    { label: 'Mountain (MT)', value: 'America/Denver' },
    { label: 'Arizona (MST)', value: 'America/Phoenix' },
    { label: 'Pacific (PT)', value: 'America/Los_Angeles' },
    { label: 'Alaska (AKT)', value: 'America/Anchorage' },
    { label: 'Hawaii (HST)', value: 'Pacific/Honolulu' },
    { label: 'UTC', value: 'UTC' },
];

const TZ_LABELS: Record<string, string> = Object.fromEntries(TIMEZONE_OPTIONS.map((o) => [o.value, o.label]));
const TIME_LABELS: Record<string, string> = Object.fromEntries(TIME_OPTIONS.map((o) => [o.value, o.label]));
const DOW_LABELS: Record<number, string> = Object.fromEntries(DAY_OF_WEEK_OPTIONS.map((o) => [o.value, o.label]));

// Detect the viewer's timezone, falling back to ET if it's outside our list.
export const defaultTimezone = (): string => {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return TZ_LABELS[tz] ? tz : 'America/New_York';
    } catch {
        return 'America/New_York';
    }
};

// Short tz abbreviation from the curated label, e.g. 'America/New_York' → 'ET'.
export const tzAbbrev = (tz: string): string =>
    (TZ_LABELS[tz] ?? '').match(/\(([^)]+)\)/)?.[1] ?? tz;

// Format an instant in the schedule's OWN timezone (not the viewer's), so
// "next run" reads correctly regardless of where the admin is sitting.
export const formatInTimezone = (iso: string | null | undefined, tz: string): string => {
    if (!iso) return '—';
    const d = tz ? dayjs(iso).tz(tz) : dayjs(iso);
    return `${d.format('MM/DD/YYYY hh:mm A')} ${tzAbbrev(tz)}`.trim();
};

// Plain-English schedule, e.g. "every Monday at 9:00 AM ET" — used in the
// form summary and the list.
export const describeCadence = (s: {
    cadence: ScheduledReportTypes.Cadence; timeOfDay: string; timezone: string; dayOfWeek: number; dayOfMonth: number;
}): string => {
    const time = TIME_LABELS[s.timeOfDay] ?? s.timeOfDay;
    const tz = (TZ_LABELS[s.timezone] ?? s.timezone).replace(/^.*\(|\)$/g, '') || s.timezone;
    if (s.cadence === 'daily') return `every day at ${time} ${tz}`;
    if (s.cadence === 'weekly') return `every ${DOW_LABELS[s.dayOfWeek] ?? 'Monday'} at ${time} ${tz}`;
    return `on the ${ordinal(s.dayOfMonth)} of each month at ${time} ${tz}`;
};
